import { Navigate, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext.js';
import { ReactNode, useEffect, useState } from 'react';
import { api } from '@/services/api.js';

interface ProtectedRouteProps {
  children?: ReactNode;
  requiredRole?: 'ADMIN' | 'USER' | 'GUEST';
  redirectTo?: string;
}

export const ProtectedRoute = ({
  children,
  requiredRole = 'USER',
  redirectTo,
}: ProtectedRouteProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Set default redirect path if not provided
  if (!redirectTo) {
    redirectTo = requiredRole === 'ADMIN' ? '/admin/login' : '/login';
  }

  // Check authentication and role in an effect
  useEffect(() => {
    let isMounted = true;
    
    const checkAuth = async () => {
      // Check if we have a token in localStorage
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (!token || !storedUser) {
        // No token or user data, redirect to login
        if (isMounted) {
          navigate(redirectTo, { state: { from: location }, replace: true });
          setIsLoading(false);
        }
        return;
      }
      
      try {
        // Set the token in the API client
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Parse the stored user data
        const userData = JSON.parse(storedUser);
        
        // Check if user has required role
        if (isMounted) {
          if (requiredRole === 'ADMIN' && userData.role !== 'ADMIN') {
            console.log('Access denied: User is not an admin', { userRole: userData.role, requiredRole });
            navigate(redirectTo, { state: { from: location }, replace: true });
            setIsLoading(false);
            return;
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Clear invalid token and redirect to login
        if (isMounted) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete api.defaults.headers.common['Authorization'];
          navigate(redirectTo, { state: { from: location }, replace: true });
          setIsLoading(false);
        }
      }
    };

    checkAuth();
    
    return () => {
      isMounted = false;
    };
  }, [location, redirectTo, requiredRole, navigate]);

  // Show loading state during initial auth check
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Role check is now handled in the effect

  // If authorized, render the protected content
  return children ? <>{children}</> : <Outlet />;
};
